import { useState } from "react";
import { CheckCircle2, ArrowRight, Users, Clock, Shield, Building2, Star } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ScrollReveal from "@/components/ScrollReveal";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPLOYER_IMG = "https://images.unsplash.com/photo-1758518726609-c551f858cd5c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjB0ZWFtJTIwYnVzaW5lc3MlMjBtZWV0aW5nfGVufDB8fHx8MTc3NTkzMDgwNnww&ixlib=rb-4.1.0&q=85";

const benefits = [
  { icon: Users, title: "Access Top Talent", desc: "Tap into our network of 10,000+ pre-screened professionals." },
  { icon: Clock, title: "Reduce Hiring Time", desc: "Average 15-day time-to-fill vs. industry average of 42 days." },
  { icon: Shield, title: "Zero Compliance Risk", desc: "We handle all statutory compliance for contract staffing." },
  { icon: Building2, title: "Industry Expertise", desc: "Specialized recruitment teams for 15+ industry verticals." },
];

const howToEngage = [
  { step: "01", title: "Share Requirements", desc: "Tell us about the roles, skills, experience, and culture fit you need." },
  { step: "02", title: "We Source & Screen", desc: "Our team identifies, screens, and shortlists the best candidates." },
  { step: "03", title: "Interview & Select", desc: "You interview our shortlisted candidates and make your pick." },
  { step: "04", title: "Onboard & Support", desc: "We assist with onboarding and provide post-placement support." },
];

const testimonials = [
  { company: "TechCorp India", person: "VP Engineering", quote: "Skill Bridge delivered 15 engineers in under 3 weeks. Their understanding of tech hiring is exceptional." },
  { company: "Metro Healthcare", person: "HR Director", quote: "Reliable, compliant, and fast. They've been our staffing partner for 2 years now." },
  { company: "RetailMax India", person: "COO", quote: "Their bulk recruitment capabilities helped us staff 200+ positions for our expansion." },
];

export default function EmployersPage() {
  const [form, setForm] = useState({ company_name: "", contact_person: "", email: "", phone: "", industry: "", hiring_needs: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/employer-enquiry`, form);
      setSubmitted(true);
      setForm({ company_name: "", contact_person: "", email: "", phone: "", industry: "", hiring_needs: "", message: "" });
    } catch (err) {
      console.error(err);
    }
    setSubmitting(false);
  };

  return (
    <div>
      {/* Hero */}
      <section className="bg-[var(--sb-blue)] text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-orange)] mb-4 block">For Employers</span>
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight uppercase mb-6">Hire the Right Talent, Fast</h1>
              <p className="text-blue-200 text-base md:text-lg leading-relaxed mb-8">
                Partner with India's trusted HR solutions provider to build high-performing teams. From single hires to mass recruitment — we deliver.
              </p>
              <a href="#enquiry-form" data-testid="employer-hero-cta" className="bg-[var(--sb-orange)] text-white px-8 py-4 text-base font-bold uppercase tracking-wider hover:bg-[#C9661A] transition-all hover:-translate-y-1 inline-flex items-center gap-2">
                Start Hiring <ArrowRight className="w-5 h-5" />
              </a>
            </ScrollReveal>
            <ScrollReveal delay={2} className="hidden lg:block">
              <img src={EMPLOYER_IMG} alt="Corporate Team" className="w-full rounded-none border border-white/10" />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section data-testid="employer-benefits" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <ScrollReveal className="text-center mb-16">
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-blue)] mb-3 block">Why Partner With Us</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 uppercase">Benefits for Employers</h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((b, i) => (
              <ScrollReveal key={b.title} delay={i}>
                <div className="bg-white border border-slate-200 p-8 transition-all duration-300 hover:shadow-[0_10px_40px_-15px_rgba(26,95,168,0.15)] hover:-translate-y-1 hover:border-[var(--sb-blue)]/30 text-center h-full">
                  <div className="w-14 h-14 bg-slate-50 flex items-center justify-center mx-auto mb-6 text-[var(--sb-blue)]">
                    <b.icon className="w-7 h-7" />
                  </div>
                  <h4 className="font-heading text-lg font-bold text-slate-900 uppercase mb-3">{b.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{b.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* How to Engage */}
      <section className="py-20 md:py-32 bg-[#F1F5F9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <ScrollReveal className="text-center mb-16">
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-blue)] mb-3 block">Simple Process</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 uppercase">How to Engage</h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howToEngage.map((step, i) => (
              <ScrollReveal key={step.step} delay={i}>
                <div className="text-center">
                  <div className="w-14 h-14 bg-[var(--sb-blue)] text-white flex items-center justify-center mx-auto mb-6 font-heading font-bold text-xl">
                    {step.step}
                  </div>
                  <h4 className="font-heading text-lg font-bold text-slate-900 uppercase mb-3">{step.title}</h4>
                  <p className="text-slate-600 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section data-testid="employer-testimonials" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <ScrollReveal className="text-center mb-16">
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-blue)] mb-3 block">Client Feedback</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 uppercase">What Our Clients Say</h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <ScrollReveal key={t.company} delay={i}>
                <div className="bg-white border border-slate-200 p-8 h-full">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-[var(--sb-orange)] text-[var(--sb-orange)]" />)}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed mb-6 italic">"{t.quote}"</p>
                  <div>
                    <p className="font-heading font-bold text-slate-900 uppercase text-sm">{t.company}</p>
                    <p className="text-slate-500 text-xs">{t.person}</p>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Enquiry Form */}
      <section id="enquiry-form" data-testid="employer-enquiry-form" className="py-20 md:py-32 bg-[#F1F5F9]">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <ScrollReveal className="text-center mb-12">
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-blue)] mb-3 block">Get Started</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 uppercase">Employer Enquiry</h2>
          </ScrollReveal>
          {submitted ? (
            <ScrollReveal>
              <div className="bg-white border border-[var(--sb-green)] p-12 text-center">
                <CheckCircle2 className="w-16 h-16 text-[var(--sb-green)] mx-auto mb-4" />
                <h3 className="font-heading text-2xl font-bold text-slate-900 uppercase mb-2">Thank You!</h3>
                <p className="text-slate-600">Your enquiry has been submitted. Our team will contact you within 24 hours.</p>
              </div>
            </ScrollReveal>
          ) : (
            <ScrollReveal>
              <form onSubmit={handleSubmit} className="bg-white border border-slate-200 p-8 md:p-10 space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Company Name *</label>
                    <Input data-testid="employer-company-input" required value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} className="bg-slate-50 border-slate-200 focus:border-[var(--sb-blue)] focus:ring-[var(--sb-blue)] rounded-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Contact Person *</label>
                    <Input data-testid="employer-contact-input" required value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} className="bg-slate-50 border-slate-200 focus:border-[var(--sb-blue)] focus:ring-[var(--sb-blue)] rounded-none" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Email *</label>
                    <Input data-testid="employer-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-slate-50 border-slate-200 focus:border-[var(--sb-blue)] focus:ring-[var(--sb-blue)] rounded-none" />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Phone *</label>
                    <Input data-testid="employer-phone-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-slate-50 border-slate-200 focus:border-[var(--sb-blue)] focus:ring-[var(--sb-blue)] rounded-none" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Industry</label>
                    <Select value={form.industry} onValueChange={(val) => setForm({ ...form, industry: val })}>
                      <SelectTrigger data-testid="employer-industry-select" className="bg-slate-50 border-slate-200 rounded-none">
                        <SelectValue placeholder="Select Industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {["IT", "Banking & Finance", "Manufacturing", "Healthcare", "Retail & FMCG", "Construction", "Logistics", "Education", "Other"].map((ind) => (
                          <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Hiring Needs</label>
                    <Select value={form.hiring_needs} onValueChange={(val) => setForm({ ...form, hiring_needs: val })}>
                      <SelectTrigger data-testid="employer-needs-select" className="bg-slate-50 border-slate-200 rounded-none">
                        <SelectValue placeholder="Select Type" />
                      </SelectTrigger>
                      <SelectContent>
                        {["Permanent Staffing", "Contract Staffing", "Executive Search", "Bulk Recruitment", "HR Consulting", "Payroll Management"].map((need) => (
                          <SelectItem key={need} value={need}>{need}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2 uppercase tracking-wide">Message</label>
                  <Textarea data-testid="employer-message-textarea" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-slate-50 border-slate-200 focus:border-[var(--sb-blue)] focus:ring-[var(--sb-blue)] rounded-none" placeholder="Tell us about your hiring requirements..." />
                </div>
                <button type="submit" disabled={submitting} data-testid="employer-submit-btn" className="bg-[var(--sb-blue)] text-white px-8 py-4 text-base font-bold uppercase tracking-wider hover:bg-[var(--sb-blue-hover)] transition-all hover:-translate-y-1 inline-flex items-center gap-2 disabled:opacity-50">
                  {submitting ? "Submitting..." : "Submit Enquiry"} <ArrowRight className="w-5 h-5" />
                </button>
              </form>
            </ScrollReveal>
          )}
        </div>
      </section>
    </div>
  );
}
